import { useAddress, useCreateAddress, useOrganisation } from '@my/directory-api/hooks';
import { useServiceTags } from '@my/tms-api/hooks';
import { showToast } from '@my/ui';
import { Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AddressEditorPanel } from './AddressEditorPanel';
import {
  EMPTY_ADDRESS_DRAFT,
  applicableLocationTags,
  buildCreateAddressRequest,
  draftTagSet,
  isAddressDraftValid,
  toggleDraftTag,
  type AddressDraft,
} from './locationDraft';

/**
 * A "+" beside a location picker that creates a new Location and hands its id
 * back, so an operator can add a stop they haven't set up yet without leaving
 * the form they're in.
 *
 * <p>
 * Sized to sit next to the order forms' `Combobox` (h-9, square slate border)
 * and coloured like the suite's other "+ Add" actions.
 * </p>
 */
export function NewLocationButton({
  organisationId,
  onCreated,
  disabled = false,
}: {
  organisationId: string | null;
  /** The new Location's id — the caller selects it. */
  onCreated: (addressId: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled || !organisationId}
        title="New location"
        aria-label="New location"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-slate-300 bg-white text-purple-600 transition-colors hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Plus className="h-4 w-4" />
      </button>
      {open && organisationId && (
        <NewLocationModal
          organisationId={organisationId}
          onClose={() => setOpen(false)}
          onCreated={(id) => {
            setOpen(false);
            onCreated(id);
          }}
        />
      )}
    </>
  );
}

/**
 * The Locations screen's "Add" form, in a modal. Same panel, same draft, same
 * validation, same service tags and the same create request — imported from
 * the Locations feature rather than rebuilt, so a location made here is
 * indistinguishable from one made on the Locations screen.
 *
 * <p>
 * Mounted only while open, so every opening starts from a fresh draft.
 * Closes on Escape or the X, but NOT on a backdrop click: a half-verified
 * address is too much typing to lose to a stray click beside the panel.
 * </p>
 */
export function NewLocationModal({
  organisationId,
  onClose,
  onCreated,
}: {
  organisationId: string;
  onClose: () => void;
  onCreated: (addressId: string) => void;
}) {
  const { t } = useTranslation(['locations', 'common']);

  // Default country from the organisation's main office, as the Locations
  // screen seeds it — so operators don't re-pick their own country every time.
  const orgQuery = useOrganisation(organisationId);
  const officeAddressQuery = useAddress(orgQuery.data?.mainOfficeAddressId ?? null);
  const defaultCountryIsoCode = officeAddressQuery.data?.countryIsoCode ?? null;

  const [draft, setDraft] = useState<AddressDraft>(EMPTY_ADDRESS_DRAFT);
  const [countryTouched, setCountryTouched] = useState(false);
  // The office address may arrive after the modal opens; apply it unless the
  // operator has already chosen a country (or a place, which sets one).
  useEffect(() => {
    if (!defaultCountryIsoCode || countryTouched) return;
    setDraft((d) => (d.placeId ? d : { ...d, countryIsoCode: defaultCountryIsoCode }));
  }, [defaultCountryIsoCode, countryTouched]);

  const serviceTagsQuery = useServiceTags(organisationId);
  const applicableTags = useMemo(
    () => applicableLocationTags(serviceTagsQuery.data),
    [serviceTagsQuery.data],
  );
  const tagSet = useMemo(() => draftTagSet(draft.serviceTags), [draft.serviceTags]);

  const createMutation = useCreateAddress();
  const isBusy = createMutation.isPending;
  const isValid = isAddressDraftValid(draft);

  const close = () => {
    if (!isBusy) onClose();
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !createMutation.isPending) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, createMutation.isPending]);

  const handleChange = (next: AddressDraft) => {
    if (next.countryIsoCode !== draft.countryIsoCode) setCountryTouched(true);
    setDraft(next);
  };

  const handleSave = async () => {
    if (!isValid) return;
    try {
      const id = await createMutation.mutateAsync(buildCreateAddressRequest(organisationId, draft));
      showToast({ type: 'success', message: t('locations:addresses.created') });
      onCreated(id);
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('locations:addresses.saveFailed'),
      });
    }
  };

  return createPortal(
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-location-title"
        className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-md bg-white p-4 shadow-xl"
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <div>
            <h2 id="new-location-title" className="text-sm font-semibold text-slate-900">
              New location
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Saved to Locations and picked for this task.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="shrink-0 rounded-full p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <AddressEditorPanel
          draft={draft}
          onChange={handleChange}
          onSave={() => void handleSave()}
          onCancel={close}
          isBusy={isBusy}
          isValid={isValid}
          applicableTags={applicableTags}
          tagSet={tagSet}
          onToggleTag={(tag) => setDraft((d) => toggleDraftTag(d, tag))}
        />
      </div>
    </div>,
    document.body,
  );
}
